import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';
import {
  Stethoscope,
  FileText,
  AlertTriangle,
  Briefcase,
  GraduationCap,
  Globe,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  BookOpen,
  Scale,
  Building2,
  Clock,
} from 'lucide-react';

const IMGGuidePage = () => {
  const guides = [
    {
      icon: AlertTriangle,
      title: 'Medical Training Prioritisation Act 2026',
      description: 'Understand how the new Act affects IMGs and what you need to do to qualify for specialty training.',
      link: '/img-guide/medical-training-prioritisation-act-2026',
      priority: 'critical',
      tag: 'Must Read',
    },
    {
      icon: Building2,
      title: 'Locally Employed Doctor (LED) Jobs',
      description: 'Everything you need to know about LED positions - the most accessible route for IMGs to gain NHS experience.',
      link: '/img-guide/led-jobs',
      priority: 'high',
      tag: 'Popular',
    },
    {
      icon: Briefcase,
      title: 'Trust Grade Doctor Jobs',
      description: 'A complete guide to Trust Grade positions, pay scales, and how they help build your NHS portfolio.',
      link: '/img-guide/trust-grade-jobs',
      priority: 'high',
      tag: 'Popular',
    },
  ];

  const quickFacts = [
    {
      icon: Scale,
      title: 'The New Priority System',
      description: 'UK graduates now have legal priority for Foundation and Specialty Training posts. IMGs must build NHS experience through LED/Trust Grade roles.',
    },
    {
      icon: Clock,
      title: 'Time is Critical',
      description: 'The Act takes effect in 2026. IMGs need to secure NHS positions now to build the required experience for future training applications.',
    },
    {
      icon: GraduationCap,
      title: 'Experience Counts',
      description: 'NHS experience in LED, Trust Grade, or Staff Grade roles counts towards your eligibility for specialty training under the secondary priority system.',
    },
    {
      icon: Globe,
      title: 'Visa Sponsorship Available',
      description: 'Many NHS Trusts offer Skilled Worker visa sponsorship for LED and Trust Grade positions. Check individual job listings for details.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>IMG Guide: Complete Resource for International Medical Graduates | TRACJobsAlert</title>
        <meta name="description" content="Complete guide for International Medical Graduates (IMGs) working in the NHS. Learn about the Medical Training Prioritisation Act 2026, LED jobs, Trust Grade positions, GMC registration, and building NHS experience." />
        <meta name="keywords" content="IMG guide UK, international medical graduate NHS, LED jobs guide, Trust Grade jobs guide, Medical Training Prioritisation Act, IMG NHS experience, GMC registration IMG, PLAB doctor jobs" />
        <link rel="canonical" href="https://tracjobsalert.co.uk/img-guide" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Stethoscope className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-lg text-slate-900">TRACJobsAlert</span>
                  <span className="text-xs text-emerald-600">IMG Guide</span>
                </div>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 rounded-full">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
                    Get Job Alerts
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Urgency Banner */}
        <div className="bg-amber-500 text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
            <span className="text-lg">⚠️</span>
            <p className="text-sm sm:text-base font-medium">
              <strong>Important:</strong> The Medical Training Prioritisation Act 2026 changes everything for IMGs.
              <Link to="/img-guide/medical-training-prioritisation-act-2026" className="underline ml-2 hover:text-amber-100">
                Read the full guide →
              </Link>
            </p>
          </div>
        </div>

        {/* Hero Section */}
        <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4 mr-2" />
              Comprehensive Resource for IMGs
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              Complete Guide for{' '}
              <span className="text-emerald-500">International Medical Graduates</span>{' '}
              Working in the NHS
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Everything you need to know about finding NHS jobs, building experience, and navigating 
              the new Medical Training Prioritisation Act as an IMG in the UK.
            </p>
          </div>
        </section>

        {/* Quick Facts Grid */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              What Every IMG Needs to Know in 2026
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickFacts.map((fact, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-slate-200">
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                    <fact.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{fact.title}</h3>
                  <p className="text-slate-600 text-sm">{fact.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guides Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Essential Guides for IMGs
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              In-depth resources to help you understand the UK medical job market and secure your first NHS position.
            </p>
            
            <div className="space-y-6">
              {guides.map((guide, index) => (
                <Link
                  key={index}
                  to={guide.link}
                  className="block bg-white p-6 rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      guide.priority === 'critical' ? 'bg-red-500/10' : 'bg-emerald-500/10'
                    }`}>
                      <guide.icon className={`w-7 h-7 ${
                        guide.priority === 'critical' ? 'text-red-600' : 'text-emerald-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">
                          {guide.title}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          guide.priority === 'critical' 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {guide.tag}
                        </span>
                      </div>
                      <p className="text-slate-600">{guide.description}</p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-emerald-500">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Don't Miss Out on NHS Opportunities
            </h2>
            <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
              LED and Trust Grade positions fill within 48 hours. Get instant alerts when new IMG-friendly roles are posted.
            </p>
            <Link to="/register">
              <Button className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-semibold">
                Get Job Alerts Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-emerald-200 text-sm mt-4">
              Join thousands of IMGs already receiving instant job alerts
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-white">TRACJobsAlert</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              </div>
              <p className="text-sm">© 2025 TRACJobsAlert. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default IMGGuidePage;
